import { Head, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useRef } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface ExpenseData {
  user_id: number | null
  expense_date: string | null
  expense_type_ref_id: number | null
  other_type: string
  paid_by: string
  paid_to: string
  preapproved: boolean
  amount: number
  notes: string
  receipt?: File | null
  receipt_filename?: string | null
}

interface PaymentMethod {
  value: string
  label: string
}

interface Props {
  customer: Customer
  expense: ExpenseData
  users: { id: number; name: string }[]
  expense_types: { id: number; name: string }[]
  payment_methods: PaymentMethod[]
  can_select_employee: boolean
  errors?: Record<string, string[]>
}

const OTHER_TYPE_ID = 10

export default function New({
  customer,
  expense,
  users,
  expense_types,
  payment_methods,
  can_select_employee,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Expenses", href: `/customers/${customer.id}/expenses` },
    { title: "New", href: `/customers/${customer.id}/expenses/new` },
  ]

  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, setData, post, processing, transform } = useForm<ExpenseData>({
    user_id: expense.user_id,
    expense_date: expense.expense_date,
    expense_type_ref_id: expense.expense_type_ref_id,
    other_type: expense.other_type,
    paid_by: expense.paid_by,
    paid_to: expense.paid_to,
    preapproved: expense.preapproved,
    amount: expense.amount,
    notes: expense.notes,
    receipt: null,
  })

  transform((data) => ({ case_expense: data }))

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(`/customers/${customer.id}/expenses`, { forceFormData: true })
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Expense - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Expense</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                {can_select_employee && (
                  <div>
                    <label className={labelClass}>Employee</label>
                    <select
                      className={inputClass}
                      value={data.user_id ?? ""}
                      onChange={(e) =>
                        setData(
                          "user_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      required
                    >
                      <option value="">Select employee...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                    {errors?.user_id && (
                      <p className={errorClass}>{errors.user_id[0]}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Type</label>
                  <select
                    className={inputClass}
                    value={data.expense_type_ref_id ?? ""}
                    onChange={(e) =>
                      setData(
                        "expense_type_ref_id",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    required
                  >
                    <option disabled value="">
                      --- SELECT ---
                    </option>
                    {expense_types.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {errors?.expense_type_ref_id && (
                    <p className={errorClass}>
                      {errors.expense_type_ref_id[0]}
                    </p>
                  )}
                </div>

                {data.expense_type_ref_id === OTHER_TYPE_ID && (
                  <div>
                    <label className={labelClass}>Other Type</label>
                    <input
                      type="text"
                      className={inputClass}
                      maxLength={50}
                      value={data.other_type}
                      onChange={(e) => setData("other_type", e.target.value)}
                    />
                    {errors?.other_type && (
                      <p className={errorClass}>{errors.other_type[0]}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Payment Method</label>
                  <select
                    className={inputClass}
                    value={data.paid_by}
                    onChange={(e) => setData("paid_by", e.target.value)}
                    required
                  >
                    <option value="">Select payment method...</option>
                    {payment_methods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  {errors?.paid_by && (
                    <p className={errorClass}>{errors.paid_by[0]}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="preapproved"
                    className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
                    checked={data.preapproved}
                    onChange={(e) => setData("preapproved", e.target.checked)}
                  />
                  <label
                    htmlFor="preapproved"
                    className="text-hpi-dark-gray ml-2 text-sm"
                  >
                    Pre-approved
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={data.expense_date ?? ""}
                    onChange={(e) => setData("expense_date", e.target.value)}
                    required
                  />
                  {errors?.expense_date && (
                    <p className={errorClass}>{errors.expense_date[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Paid To</label>
                  <input
                    type="text"
                    className={inputClass}
                    maxLength={50}
                    value={data.paid_to}
                    onChange={(e) => setData("paid_to", e.target.value)}
                    required
                  />
                  {errors?.paid_to && (
                    <p className={errorClass}>{errors.paid_to[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={data.amount || ""}
                    onChange={(e) =>
                      setData("amount", parseFloat(e.target.value) || 0)
                    }
                    required
                  />
                  {errors?.amount && (
                    <p className={errorClass}>{errors.amount[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Receipt</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="text-hpi-dark-gray file:bg-hpi-dark-blue hover:file:bg-hpi-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      setData("receipt", file)
                    }}
                  />
                  <p className="text-hpi-med-gray mt-1 text-xs">
                    PDF, JPEG, or PNG up to 10 MB
                  </p>
                  {errors?.receipt && (
                    <p className={errorClass}>{errors.receipt[0]}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>Explain Expense</label>
              <textarea
                className={inputClass}
                rows={4}
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                required
              />
              {errors?.notes && <p className={errorClass}>{errors.notes[0]}</p>}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => router.get(`/customers/${customer.id}/expenses`)}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Add Expense"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
